import {
  Ban,
  CheckCircle2,
  History,
  PackageCheck,
  RotateCcw,
  Settings,
  ShieldAlert,
  Undo2,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePermission } from '../../../../auth/AuthContext';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { Menu } from '../../../../components/Menu';
import { PreviewGrid } from '../../../../components/PreviewGrid';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StackedCell } from '../../../../components/table/StackedCell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { addressPathLabel } from '../../../../lib/addressApi';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime, toIso } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { personnelApi, personsApi } from '../../../human-resources/personnel/personnelApi';
import { IssuanceFormPreview } from '../components/IssuanceFormPreview';
import { RecordReturnModal } from '../components/RecordReturnModal';
import { issuancesApi } from '../issuanceApi';
import type { IssuanceDetail, IssuanceItemLine, IssuanceReturnLine } from '../issuanceTypes';
import {
  ISSUANCE_STATUS,
  ISSUANCE_STATUS_LABEL,
  PROPERTY_CLASSIFICATION_LABEL,
  isCustody,
} from '../issuanceTypes';

/*
  NO RETURNS TAB — product call, 08 Oct 2026, in the reader's own words: *remove the returns
  tab. instead, add column in the lines, the return detail*. A return is an event against ONE
  line, so it is read on that line's row rather than in a second table the reader had to match
  back to the first.
*/
/*
  `form` is the ICS or PAR the slip was signed against — captured from the hospital system on
  13 Oct 2026. Drawn only on a custody slip: a dispense signs neither form, so a tab for one
  would open onto a document that does not exist.
*/
type TabKey = 'info' | 'lines' | 'form';

/** Personnel id → display name. The three people an issuance can name, resolved in ONE batch. */
type Signatories = Record<string, string>;

/**
 * One release of stock out of a store.
 *
 * On the **header** layout: an issuance reads as one continuous thing — a slip — rather than a
 * record whose content splits into panels a reader chooses between.
 *
 * **No card carries an Edit, and that is the module's shape rather than unfinished work.**
 * There is no `issuances.update` and no update endpoint: an issuance records what physically
 * happened, so the two things worth doing to one afterwards are recording a return (the stock
 * came back) and deleting it (the slip was keyed against the wrong record). Both are
 * record-level and both sit in the header's cog, where an action about the whole record belongs
 * on a page whose tabs the reader moves between.
 *
 * **The signatories are resolved here and nowhere else.** The row carries personnel ids, and
 * turning one into a name is a two-hop through the identity store; doing it for ONE record on
 * screen is the call `RealProperty`'s custodian already takes, and it is why the list cannot
 * show or sort by those names.
 */
export function IssuanceProfilePage() {
  const { id } = useParams<{ id: string }>();
  const canReadPersonnel = usePermission('personnel.read');

  const [issuance, setIssuance] = useState<IssuanceDetail | null>(null);
  const [names, setNames] = useState<Signatories>({});

  /** The external contact, resolved from a `PersonId` rather than through a personnel row. */
  const [contactName, setContactName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [returning, setReturning] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      issuancesApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setIssuance(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError ? caught : new ApiError('Could not load this issuance.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  const accountableId = issuance?.accountablePersonnelId ?? null;
  /*
    RECEIVED BY IS NOT RESOLVED, because nothing on this page draws it any more — see the
    Signatories card. Resolving a name no row renders would be a reveal nobody asked for,
    and on this page it would also widen the batch the identity store is asked for.
  */
  const issuedById = issuance?.issuedByPersonnelId ?? null;
  const contactPersonId = issuance?.externalPartyContactPersonId ?? null;

  /*
    The two-hop: the issuance names PERSONNEL records, the identity store is keyed by PERSON.

    ONE batch resolve for the page, never one per row — that cap is the control that stops a
    leaked token walking the table. Guarded on the key, because a reader who may see the
    register is not thereby a reader of personnel, and a failed resolve leaves the row stating
    the absence rather than failing the page.
  */
  useEffect(() => {
    const ids = [accountableId, issuedById].filter(
      (value): value is string => value !== null,
    );

    if ((ids.length === 0 && contactPersonId === null) || !canReadPersonnel) {
      setNames({});
      setContactName(null);
      return;
    }

    const controller = new AbortController();

    void (async () => {
      try {
        const records = await Promise.all(
          ids.map((personnelId) => personnelApi.get(personnelId, controller.signal)),
        );

        /*
          ONE call for both kinds of person on this page.

          The three signatories arrive as PERSONNEL ids and have just been hopped to their
          `PersonId`s; the external contact IS one already, because they do not work here. A
          second resolve would be a second request against the cap that exists to stop a
          leaked token walking the identity store.
        */
        const resolved = await personsApi.resolve(
          [
            ...records.map((personnel) => personnel.personId),
            ...(contactPersonId === null ? [] : [contactPersonId]),
          ],
          controller.signal,
        );

        if (controller.signal.aborted) return;

        const byPerson = new Map(resolved.map((person) => [person.personId, person.displayName]));

        setContactName(contactPersonId === null ? null : byPerson.get(contactPersonId) ?? null);

        setNames(
          Object.fromEntries(
            records.map((personnel) => [
              personnel.id,
              // The employee number is the honest fallback for an erased or unresolvable
              // identity: the record still names somebody, this page cannot print who.
              byPerson.get(personnel.personId) ?? personnel.employeeNumber,
            ]),
          ),
        );
      } catch {
        // Left as it was — an absent name is stated by the row rather than by an error state.
      }
    })();

    return () => controller.abort();
  }, [accountableId, issuedById, contactPersonId, canReadPersonnel]);

  /**
   * Puts a deleted slip back.
   *
   * **No confirm tick and no password** — restoring puts a record back, and ceremony on an undo
   * just trains people to click through ceremony. The issuance number is permanent and was never
   * freed, so unlike a stock unit's serial there is nothing here for a restore to collide with.
   */
  async function restore() {
    if (!id) return;

    setRestoring(true);
    setError(null);

    try {
      await issuancesApi.activate(id);
      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not restore this issuance.', 0),
      );
    } finally {
      setRestoring(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Logistics',
    'Inventory',
    { label: 'Issuances', to: '/inventory/issuances' },
    issuance?.issuanceNumber ?? 'Issuance',
  ];

  // Guarded on having NOTHING to show, never on `error` alone: once a record is up it is still
  // the last good answer, and a failed refresh belongs in the banner.
  if (error && !issuance) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  if (loading || !issuance) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const record = issuance;
  const custody = isCustody(record.issuanceType);

  // Each line's returns, oldest first — the server sends them newest first for the whole slip.
  const returnsByLine = new Map<string, IssuanceReturnLine[]>();
  for (const event of [...record.returns].reverse()) {
    returnsByLine.set(event.issuanceItemId, [
      ...(returnsByLine.get(event.issuanceItemId) ?? []),
      event,
    ]);
  }

  /**
   * The banner, and every state renders.
   *
   * A strip that appears only when something is wrong teaches the reader to skip it, and then it
   * is missed on the day it matters. Amber is legal on a PROFILE — the no-yellow rule is about
   * an index row, where the brand colour reads as chrome the shell put there.
   */
  function banner() {
    if (error) {
      return {
        tone: 'red' as const,
        icon: <Ban className="h-4 w-4" aria-hidden="true" />,
        message: error.message,
      };
    }

    if (!record.isActive) {
      return {
        tone: 'red' as const,
        icon: <Ban className="h-4 w-4" aria-hidden="true" />,
        message:
          'This issuance is deleted, so it is out of the register. The stock was NOT put back — deleting corrects the record, and the ledger still says what moved.',
      };
    }

    if (record.isOverdue) {
      return {
        tone: 'red' as const,
        icon: <ShieldAlert className="h-4 w-4" aria-hidden="true" />,
        message: `Due back on ${formatDateOnly(record.expectedReturnDate)}, and ${record.outstandingQuantity} of ${record.totalQuantity} is still out.`,
      };
    }

    if (record.status === ISSUANCE_STATUS.completed) {
      return {
        tone: 'slate' as const,
        icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" />,
        message:
          'Dispensed — the stock was handed over to be used up. Nothing is expected back, so there is no return to record.',
      };
    }

    if (record.status === ISSUANCE_STATUS.returned) {
      return {
        tone: 'slate' as const,
        icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" />,
        message: `All ${record.totalQuantity} has come back and is available again.`,
      };
    }

    return {
      tone: 'amber' as const,
      icon: <PackageCheck className="h-4 w-4" aria-hidden="true" />,
      message: `${record.outstandingQuantity} of ${record.totalQuantity} is still out${
        record.expectedReturnDate ? `, due back ${formatDateOnly(record.expectedReturnDate)}` : ''
      }.`,
    };
  }

  const state = banner();

  // `!= null`: a null off the wire arrives as undefined.
  const formKind = record.acknowledgementType != null ? record.acknowledgementType : null;


  /**
   * The record-level cog.
   *
   * **Record return is ABSENT for a dispense rather than disabled**, which is the hide rule read
   * correctly: a consumable that was used up is not coming back, so the entry does not apply to
   * this record at all. On a custody issuance with nothing left out it IS drawn and disabled,
   * because that is state rather than a gate.
   */
  const actions = [
    ...(custody
      ? [
          {
            key: 'record-return',
            label: 'Record return',
            icon: <Undo2 className="h-4 w-4" aria-hidden="true" />,
            permission: 'issuances.return',
            disabledReason: !record.isActive
              ? 'This issuance is deleted. Restore it before recording anything against it.'
              : record.outstandingQuantity > 0
                ? null
                : 'Everything on this issuance is already back.',
            onClick: () => setReturning(true),
          },
        ]
      : []),
    {
      /*
        The record-scoped history, scoped with `entityId` and NOT with a type list — a write the
        server refused never reached an entity, so a type bound would drop exactly the event a
        reader opens this for. Its key is the audit module's: a control carries the key of what
        it opens.
      */
      key: 'history',
      label: 'History Logs',
      icon: <History className="h-4 w-4" aria-hidden="true" />,
      permission: 'audit-logs.read',
      onClick: () => setHistoryOpen(true),
    },
  ];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="issuance-profile"
        header={
          <ProfileHeader
            id="issuance-profile-header"
            icon={<PackageCheck className="h-6 w-6" aria-hidden="true" />}
            title={record.issuanceNumber}
            meta={[
              record.issuanceTypeLabel,
              record.acknowledgementType === null || record.controlNumber === null
                ? null
                : `${PROPERTY_CLASSIFICATION_LABEL[record.acknowledgementType]} ${record.controlNumber}`,
              record.receivingOrgUnitName ?? record.externalPartyName,
            ]}
            secondary={[
              `${record.totalQuantity} issued across ${record.lineCount} line${record.lineCount === 1 ? '' : 's'}`,
              custody ? `${record.outstandingQuantity} still out` : null,
              record.totalValue === null ? null : formatMoney(record.totalValue),
            ]}
            status={
              <StatusText tone={record.isActive ? 'active' : 'inactive'}>
                {record.isActive ? ISSUANCE_STATUS_LABEL[record.status] : 'Deleted'}
              </StatusText>
            }
            createdBy={record.createdByName}
            createdAt={record.createdAt}
            actions={
              <Menu
                id="issuance-profile-settings"
                items={actions}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Issuance settings"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          <ProfileBanner
            id="issuance-profile-banner"
            icon={state.icon}
            message={state.message}
            action={
              !record.isActive && !error ? (
                // Gates the button, not the banner: a read-only viewer still needs the state.
                <PermissionGate permission="issuances.delete">
                  <Button
                    id="issuance-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              ) : undefined
            }
          />
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'lines',
            label: (
              <>
                Lines <CountBadge count={record.items.length} />
              </>
            ),
          },
          /*
            PREVIEW, named for what the tab does — the Repository's own word for a document
            shown in place. Drawn only on a custody slip: a dispense signs no form.
          */
          ...(formKind === null ? [] : [{ key: 'form' as const, label: 'Preview' }]),
        ]}
        // `bleed` hands the preview the whole height; its card stands on its own `p-4` inset.
        panel={tab === 'form' ? 'bleed' : 'cards'}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === 'info' ? (
          <>
            <ProfileCard id="issuance-details" title="Issuance">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Number', value: record.issuanceNumber, mono: true },
                  { label: 'Method', value: record.issuanceTypeLabel },
                  {
                    /*
                      Which accountability form the slip was signed against. DERIVED from value on
                      the server and never stored on the stock, which is why a slip can
                      legitimately carry lines of both.
                    */
                    label: 'Form',
                    value:
                      record.acknowledgementType === null
                        ? null
                        : PROPERTY_CLASSIFICATION_LABEL[record.acknowledgementType],
                  },
                  { label: 'ICS / PAR no.', value: record.controlNumber, mono: true },
                  { label: 'Reference', value: record.referenceNumber, mono: true },
                  { label: 'Purpose', value: record.purpose },
                  { label: 'Notes', value: record.notes },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="issuance-recipient" title="Recipient">
              <PreviewTable
                variant="table-display"
                rows={[
                  {
                    /*
                      `Hidden` rather than an em dash for a reader without `personnel.read`: the
                      record HAS an officer, this page has not been given the name. An em dash
                      would state that nobody is accountable, which is a different claim.
                    */
                    label: 'Accountable officer',
                    value: personName(accountableId, names, canReadPersonnel),
                  },
                  { label: 'Receiving unit', value: record.receivingOrgUnitName },
                  { label: 'Lent to', value: record.externalPartyName },
                  { label: 'Contact number', value: record.externalPartyContact },
                  {
                    /*
                      RESOLVED, never stored. The row carries a `PersonId` and this page asks
                      the identity store for a name, exactly as it does for the three
                      signatories — the only difference is that this one is not an employee, so
                      there is no personnel row to hop through.
                    */
                    label: 'Contact person',
                    value:
                      contactPersonId === null
                        ? null
                        : canReadPersonnel
                          ? contactName ?? 'Name unavailable'
                          : 'Hidden',
                  },
                  {
                    /*
                      ONE ROW for the path, joined the way every address in this product is
                      joined — commas, through the one helper, so the picker that wrote it and
                      the card that reads it cannot disagree about how an address reads.
                    */
                    label: 'Address',
                    value: addressPathLabel({
                      province: record.externalPartyProvince,
                      cityMunicipality: record.externalPartyCityMunicipality,
                      barangay: record.externalPartyBarangay,
                    // The helper joins what is there and returns an empty string for nothing at
                    // all; a PreviewTable row wants null, which is what draws the em dash.
                    }) || null,
                  },
                  { label: 'Lot No / Street / Bldg', value: record.externalPartyStreetAddress },
                  {
                    // A CALENDAR date, never the instant path — a due date names a day.
                    label: 'Due back',
                    value:
                      record.expectedReturnDate === null
                        ? null
                        : formatDateOnly(record.expectedReturnDate),
                    mono: record.expectedReturnDate !== null,
                  },
                ]}
              />
            </ProfileCard>

            {/*
              RECEIVED BY IS NO LONGER SHOWN — product call, 06 Oct 2026, in the reader own
              words: *remove the received by*.

              THE ROWS COME OFF IN THE SAME CHANGE AS THE FORM FIELD, which is the
              competency-development block call: a row that is an em dash on every new record
              says nothing and costs the width the record own facts need. Restoring the
              question means restoring these two rows in the same change.

              THE DATA IS NOT DESTROYED. The columns, the request field and the wire fields
              all stay, so the seven slips recorded before this keep whoever they named — it
              is reachable from the audit trail and from the database, and not from here.
            */}
            <ProfileCard id="issuance-signatories" title="Signatories">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Issued by', value: personName(issuedById, names, canReadPersonnel) },
                  {
                    /*
                      THE POST AS IT WAS WHEN THEY SIGNED, not as it is now.

                      A snapshot, so promoting somebody does not rewrite the slip they put
                      their name to. It is derived server-side off the substantive line of
                      service, which is why the form never asks for it.
                    */
                    label: 'Position',
                    value: record.issuedByPosition,
                  },
                ]}
              />
            </ProfileCard>

            {/*
              WHAT IS PRINTED AT THE HEAD OF THE FORM.

              DRAWN ONLY WHERE A FORM WAS SIGNED. A dispense signs neither an ICS nor a PAR, so
              the card would be two em dashes under a heading naming a document that does not
              exist — which is the conditional-SECTION call, one card rather than one panel.
            */}
            {record.acknowledgementType === null ? null : (
              <ProfileCard id="issuance-form-header" title="ICS / PAR form">
                <PreviewTable
                  variant="table-display"
                  rows={[
                    {
                      /*
                        THE ACCOUNTABLE OFFICER OWN OFFICE, derived at release and
                        snapshotted — the form stopped asking for it on 06 Oct 2026, so this
                        row states a value the server worked out rather than one somebody
                        typed. NULL where the officer holds no live primary line of service.
                      */
                      label: 'LGU / Office',
                      value: record.lguName,
                    },
                    { label: 'Fund cluster', value: record.fundCluster },
                  ]}
                />
              </ProfileCard>
            )}

            {/* Audit takes no Edit at any permission level: it records what happened. */}
            <ProfileCard id="issuance-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Issuance ID', value: record.id, mono: true },
                  { label: 'Created by', value: record.createdByName },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(record.createdAt)}>
                        {formatDateTime(record.createdAt)}
                      </time>
                    ),
                    mono: true,
                  },
                  { label: 'Updated by', value: record.updatedByName },
                  {
                    label: 'Updated',
                    value:
                      record.updatedAt === null ? null : (
                        <time dateTime={toIso(record.updatedAt)}>
                          {formatDateTime(record.updatedAt)}
                        </time>
                      ),
                    mono: record.updatedAt !== null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        ) : null}

        {tab === 'lines' ? (
          <ProfileCard id="issuance-lines" title="What went out">
            <PreviewGrid<IssuanceItemLine>
              rows={record.items}
              rowKey={(line) => line.id}
              empty="This issuance has no lines."
              columns={[
                {
                  /*
                    THE PRODUCT, never a serial or a SKU standing in for it — product call,
                    08 Oct 2026: *i must be able [to see] the exact name/brand model … so that i
                    wont have to guess*. The Issued Assets panel's own shape, column for column:
                    make and model over the governed type name (dropped where it IS the label),
                    then SKU, then the serial on its own. Stacking is legal here — a
                    `<PreviewGrid>` provides no index-table surface.
                  */
                  header: 'Item',
                  width: 240,
                  render: (line) => (
                    <StackedCell
                      primary={line.itemIdentity}
                      secondary={line.itemIdentity === line.itemName ? undefined : line.itemName}
                    />
                  ),
                },
                {
                  header: 'SKU',
                  width: 130,
                  render: (line) => <TextCell value={line.sku} mono />,
                },
                {
                  // Null on a quantity line — counted stock has no article to name.
                  header: 'Serial no.',
                  width: 150,
                  render: (line) => <TextCell value={line.serialNumber} mono />,
                },
                {
                  header: 'From',
                  width: 170,
                  render: (line) => <TextCell value={line.storageFacilityName} />,
                },
                {
                  header: 'Qty',
                  width: 90,
                  align: 'right',
                  render: (line) => (
                    <TextCell
                      value={`${line.quantity}${line.unitOfMeasureCode ? ` ${line.unitOfMeasureCode}` : ''}`}
                      mono
                      numeric
                    />
                  ),
                },
                {
                  // An em dash on a dispense: nothing is expected back, so a returned count of
                  // zero would read as a balance somebody still owes.
                  header: 'Returned',
                  width: 90,
                  align: 'right',
                  render: (line) => (
                    <TextCell value={custody ? String(line.quantityReturned) : null} mono numeric />
                  ),
                },
                {
                  /*
                    WHAT USED TO BE THE RETURNS TAB, read on the line it came back against.

                    One entry per return EVENT, oldest first, joined on one line the way
                    `Drawn from` joins its lots — a line can come back in parts, on different
                    days, into different stores. An em dash where nothing has come back, and
                    on every dispense, where nothing is expected to.
                  */
                  header: 'Return detail',
                  width: 300,
                  render: (line) => (
                    <TextCell value={returnDetail(returnsByLine.get(line.id))} />
                  ),
                },
                /*
                  WHAT THE GOVERNMENT FORM PRINTS, and every one is a JOIN on the server —
                  three off the unit, three off the catalog row. Nothing is stamped on the line.

                  DRAWN ONLY ON A CUSTODY SLIP. A dispense signs neither form, so these would be
                  columns about a document that does not exist — and the first three would be
                  em dashes on every row of it anyway, a consumable having no unit to read.
                */
                ...(custody
                  ? [
                      {
                        /*
                          NULL ON A QUANTITY LINE by construction, and that is the honest answer:
                          a property number belongs to a named article, and a lot is a count.
                        */
                        header: 'Property no.',
                        width: 150,
                        render: (line: IssuanceItemLine) => (
                          <TextCell value={line.inventoryNumber} mono />
                        ),
                      },
                      {
                        header: 'Fund',
                        width: 130,
                        render: (line: IssuanceItemLine) => <TextCell value={line.fund} />,
                      },
                      {
                        header: 'Acquired',
                        width: 120,
                        render: (line: IssuanceItemLine) => (
                          <TextCell
                            value={
                              line.acquisitionDate === null
                                ? null
                                : formatDateOnly(line.acquisitionDate)
                            }
                            mono
                          />
                        ),
                      },
                      {
                        /*
                          THE FORM ASKS ONE QUESTION AND THE ROW ANSWERS IT ONE OF TWO WAYS.

                          A thing either spoils on a date or wears out over a span, and the two
                          are mutually exclusive on the catalog row — so an item that expires
                          reads Perishable here rather than leaving the column blank and the
                          reader wondering which half is missing.
                        */
                        header: 'Est. useful life',
                        width: 130,
                        render: (line: IssuanceItemLine) => (
                          <TextCell
                            value={
                              line.estimatedUsefulLifeYears !== null
                                ? `${line.estimatedUsefulLifeYears} yr${line.estimatedUsefulLifeYears === 1 ? '' : 's'}`
                                : line.tracksExpiry
                                  ? 'Perishable'
                                  : null
                            }
                          />
                        ),
                      },
                    ]
                  : []),
                {
                  header: 'Unit cost',
                  width: 120,
                  align: 'right',
                  render: (line) => (
                    <TextCell
                      value={
                        line.unitCostSnapshot === null ? null : formatMoney(line.unitCostSnapshot)
                      }
                      mono
                      numeric
                    />
                  ),
                },
                {
                  /*
                    Which lots a quantity line drew from, in the FEFO order it drew them. This is
                    what a return credits back against, and it is why the allocations are a child
                    table rather than a JSON column — a reader tracing a consumed article can see
                    it. A serialized line has none: the unit is the thing.
                  */
                  header: 'Drawn from',
                  width: 220,
                  render: (line) => (
                    <TextCell
                      value={
                        line.allocations.length === 0
                          ? null
                          : line.allocations
                              .map((lot) => `${lot.batchNumber} (${lot.quantity})`)
                              .join(', ')
                      }
                      mono
                    />
                  ),
                },
              ]}
            />
          </ProfileCard>
        ) : null}

        {/*
          Mounted only while open — mounting is what draws the form. KEYED on the slip and on
          whether it is live, so a restore remounts the panel into its loading state rather than
          flashing a failure while the first draw is in flight.
        */}
        {tab === 'form' && formKind !== null ? (
          <IssuanceFormPreview key={`${record.id}-${record.isActive}`} issuance={record} form={formKind} />
        ) : null}
      </ProfileHeaderShell>

      {returning ? (
        <RecordReturnModal
          open
          issuance={record}
          onClose={() => setReturning(false)}
          onSaved={() => {
            setReturning(false);
            load();
          }}
        />
      ) : null}

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="issuances"
        entityId={record.id}
      />
    </div>
  );
}

/**
 * One line's returns, as one sentence per event — `1 on Oct 07, 2026 into Central Supply Store
 * (Good)`. The quantity leads because a line can come back in parts; the condition is the
 * reader's free text, so it is quoted as given and left off where nobody recorded one.
 */
function returnDetail(events: IssuanceReturnLine[] | undefined): string | null {
  if (!events || events.length === 0) return null;

  return events
    .map(
      (event) =>
        `${event.quantity} on ${formatDateOnly(event.returnedOn)} into ${event.storageFacilityName}${
          event.condition ? ` (${event.condition})` : ''
        }`,
    )
    .join('; ');
}

/**
 * What a personnel id reads as on this page.
 *
 * Three states, and they are different facts: no id is an em dash (nobody was named), an id
 * without the key is `Hidden` (somebody was named, this page may not say who), and an id whose
 * resolve has not landed is null rather than a guess.
 */
function personName(
  personnelId: string | null,
  names: Signatories,
  canReadPersonnel: boolean,
): string | null {
  if (personnelId === null) return null;
  if (!canReadPersonnel) return 'Hidden';

  return names[personnelId] ?? null;
}
